import { useNavigate, useParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { GetDistrictById, selectCurrentDistrict, UpdateDistrict, onSetCurrentDistrict } from "../../store/districtsReducer";
import { useEffect, useState } from "react";
import s from './District.module.scss';
import Header from "../Header/Header";
import {Button, Input, Popconfirm, Spin} from 'antd';
import {LoadingOutlined} from "@ant-design/icons";
import TextArea from "antd/es/input/TextArea";
import { DistrictFormFieldsType } from "../../types/districtsTypes";
import MapForDistrictPoint from "./MapForDistrictPoint";

const isValidUrl = (url: string): boolean => {
    if (!url) return false;
    try {
        const parsedUrl = new URL(url);
        return parsedUrl.protocol === 'http:' || parsedUrl.protocol === 'https:';
    } catch {
        return false;
    }
};

const isValidLatitude = (value: string | number): boolean => {
    const num = typeof value === 'string' ? parseFloat(value) : value;
    return !isNaN(num) && num >= -90 && num <= 90;
};

const isValidLongitude = (value: string | number): boolean => {
    const num = typeof value === 'string' ? parseFloat(value) : value;
    return !isNaN(num) && num >= -180 && num <= 180;
};

const defaultFormFields = {
    district_id: 0,
    name: '',
    description: '',
    latitude: 0,
    longitude: 0,
    address: '',
    url: ''
}

const District = () => {
    const {id} = useParams()
    const dispatch = useAppDispatch()
    const district = useAppSelector(selectCurrentDistrict)
    const navigate = useNavigate()

    const [isDistrictLoading, setIsDistrictLoading] = useState(true)
    const [fieldsWithError, setFieldsWithError] = useState(false)
    const [formFields, setFormFields] = useState(defaultFormFields)
    const [isSaving, setIsSaving] = useState(false)
    const [isMapShown, setIsMapShown] = useState(false)

    useEffect(() => {
        dispatch(GetDistrictById(Number(id)))
        .then(() => {
            setIsDistrictLoading(false)
        })
    }, [dispatch, id])

    useEffect(() => {
        if (district?.district_id && !formFields?.district_id) {
            setFormFields({
                district_id: district?.district_id,
                name: district?.name,
                description: district?.description,
                latitude: district?.latitude,
                longitude: district?.longitude,
                address: district?.address,
                url: district?.url
            })
            setIsMapShown(true)
        }
    }, [district, formFields])

    useEffect(() => {
        return () => {
            dispatch(onSetCurrentDistrict(null))
        }
    }, [dispatch])

    const onFieldChange = (fieldName: string, value: string) => {
        setFormFields({
            ...formFields,
            [fieldName]: value
        })
    }

    const onSaveDistrictForm = () => {
        if (formFields?.name === ''
            // || formFields?.description === ''
            || formFields?.address === ''
            || !isValidUrl(formFields?.url)
            || !isValidLatitude(formFields?.latitude)
            || !isValidLongitude(formFields?.longitude)
        ) {
            setFieldsWithError(true)
            return
        }
        setIsSaving(true)
        dispatch(UpdateDistrict({
            id: Number(id),
            formValues: {...formFields, organization_id: district?.organization?.id!} as DistrictFormFieldsType
        }))
        .then(() => {
            setIsSaving(false)
            navigate('/districts')
        })
    }

    return (
        <div>
            <Header/>
            {isDistrictLoading ? (
                <div style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '100vh',
                    flexDirection: 'column'
                }}>
                    <Spin indicator={<LoadingOutlined style={{ fontSize: 48, color: '#1F553B' }} spin />} />
                    <span style={{marginTop: '25px'}}>
                        Die Daten des Bezirks werden geladen. Bitte warten Sie.
                    </span>
                </div>
            ) : (
                <div className={s.inner}>
                    <h1 className={s.inner__title}>
                        {district?.name}
                    </h1>
                    <div className={s.backLink} onClick={() => navigate('/districts')}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                            <path d="M14 7L9 12L14 17" stroke="#667085" strokeWidth="2" strokeLinecap="round"
                                    strokeLinejoin="round"/>
                        </svg>
                        <span>Zurück</span>
                    </div>
                    <div className={`${s.form} districtForm`}>
                        <div className={s.form__row}>
                            <label>Name</label>
                            <Input
                                placeholder={'Name eingeben'}
                                value={formFields?.name}
                                onChange={(e) => onFieldChange('name', e.target.value)}
                            />
                            {formFields?.name === '' && fieldsWithError &&
                                <p className={s.form__error}>Bitte Namen eingeben!</p>
                            }
                        </div>
                        <div className={s.form__row}>
                            <label>Beschreibung</label>
                            <TextArea placeholder={'Beschreibung eingeben'}
                                        style={{
                                            minHeight: '100px'
                                        }}
                                        value={formFields?.description}
                                        onChange={(e) => onFieldChange('description', e.target.value)}/>
                            {formFields?.description === '' && fieldsWithError &&
                                <p className={s.form__error}>Bitte Beschreibung eingeben!</p>
                            }
                        </div>
                        <div className={s.form__row}>
                            <label>Adresse</label>
                            <Input
                                placeholder={'Adresse eingeben'}
                                value={formFields?.address}
                                onChange={(e) => onFieldChange('address', e.target.value)}
                            />
                            {formFields?.address === '' && fieldsWithError &&
                                <p className={s.form__error}>Bitte Adresse eingeben!</p>
                            }
                        </div>
                        <div className={s.form__row_double}>
                            <div className={s.form__row}>
                                <label>Latitude</label>
                                <Input
                                    placeholder={'Latitude eingeben'}
                                    value={formFields?.latitude}
                                    onChange={(e) => onFieldChange('latitude', e.target.value)}
                                />
                                {!isValidLatitude(formFields?.latitude) && fieldsWithError &&
                                    <p className={s.form__error}>Bitte gültige Latitude eingeben (-90 bis 90)</p>
                                }
                            </div>
                            <div className={s.form__row}>
                                <label>Longitude</label>
                                <Input
                                    placeholder={'Longitude eingeben'}
                                    value={formFields?.longitude}
                                    onChange={(e) => onFieldChange('longitude', e.target.value)}
                                />
                                {!isValidLongitude(formFields?.longitude) && fieldsWithError &&
                                    <p className={s.form__error}>Bitte gültige Longitude eingeben (-180 bis 180)</p>
                                }
                            </div>
                        </div>
                        <div className={s.form__row}>
                            <label>Organization</label>
                            <Input
                                placeholder={'Organization eingeben'}
                                value={district?.organization?.name ?? ''}
                                disabled={true}
                            />
                        </div>
                        <div className={s.form__row}>
                            <label>URL</label>
                            <Input
                                placeholder={'URL eingeben'}
                                value={formFields?.url}
                                onChange={(e) => onFieldChange('url', e.target.value)}
                            />
                            {!isValidUrl(formFields?.url) && fieldsWithError &&
                                <p className={s.form__error}>Bitte gültige URL eingeben (z.B. https://example.com)</p>
                            }
                        </div>
                        {isMapShown && !!district?.organization.icon &&
                            <div style={{
                                marginTop: '32px',
                                borderRadius: '12px'
                            }}>
                                <MapForDistrictPoint coordinates={{lat: formFields?.latitude, lng: formFields?.longitude}}/>
                            </div>
                        }
                        <div className={s.buttons}>
                            <Popconfirm
                                title="Diese Anfrage löschen?"
                                description="Diese Aktion kann nicht rückgängig gemacht werden."
                                // onConfirm={() => handleConfirm(requestFields?.id!!)}
                                okText="Ja"
                                cancelText="Nein"
                                okType="danger"
                            >
                                <button className={s.deleteBtn} style={{visibility: 'hidden'}}>Löschen</button>
                            </Popconfirm>
                            <button className={s.cancelBtn} onClick={() => navigate('/districts')}>Abbrechen</button>
                            <Button
                                loading={isSaving}
                                className={s.saveBtn}
                                onClick={onSaveDistrictForm}
                            >
                                Speichern
                            </Button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}

export default District;
